import { useState } from 'react';
import { CheckCircle, Download, Copy, Check } from 'lucide-react';
import { generateCredentialsPDF, generateCredentialsExcel } from '@/utils/credentialsExport';

export default function MerchantCreatedResultModal({ result, onClose }) {
  const [copied, setCopied] = useState(null);
  if (!result) return null;

  const handleCopy = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const exportData = {
    merchant: result.merchant,
    project: result.project,
    api_keys: result.api_keys,
    environments: result.environments,
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="merchant-created-modal"
    >
      <div
        className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b bg-green-600">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <CheckCircle className="w-5 h-5" /> Comercio Creado Exitosamente
          </h3>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <h4 className="text-sm font-bold text-gray-800 mb-2">Datos del Comercio</h4>
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
              <div><span className="font-medium text-gray-600">Empresa:</span> {result.merchant?.company_name}</div>
              <div><span className="font-medium text-gray-600">Email:</span> {result.merchant?.email}</div>
              <div>
                <span className="font-medium text-gray-600">Contrasena:</span>{' '}
                <code className="bg-yellow-100 px-1.5 py-0.5 rounded">{result.merchant?.password}</code>
              </div>
              {result.merchant?.rif && (
                <div><span className="font-medium text-gray-600">RIF:</span> {result.merchant.rif}</div>
              )}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-bold text-gray-800 mb-2">API Keys</h4>
            <div className="space-y-2">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-amber-200 text-amber-800 rounded-full">
                  QA / Test
                </span>
                <div className="mt-1 flex items-center gap-2">
                  <code className="text-xs break-all text-gray-800 flex-1">{result.api_keys?.test?.full_key}</code>
                  <button
                    data-testid="copy-created-test-key"
                    onClick={() => handleCopy(result.api_keys?.test?.full_key, 'test')}
                    title="Copiar API Key de Test"
                    className="p-1.5 rounded hover:bg-amber-100 transition-colors flex-shrink-0"
                  >
                    {copied === 'test' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                  </button>
                </div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-green-200 text-green-800 rounded-full">
                  Produccion
                </span>
                <div className="mt-1 flex items-center gap-2">
                  <code className="text-xs break-all text-gray-800 flex-1">{result.api_keys?.live?.full_key}</code>
                  <button
                    data-testid="copy-created-live-key"
                    onClick={() => handleCopy(result.api_keys?.live?.full_key, 'live')}
                    title="Copiar API Key de Produccion"
                    className="p-1.5 rounded hover:bg-green-100 transition-colors flex-shrink-0"
                  >
                    {copied === 'live' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <span className="font-medium text-gray-600">Proyecto:</span> {result.project?.project_name}
          </div>
        </div>
        <div className="px-6 py-4 border-t bg-gray-50 flex flex-wrap items-center gap-3">
          <button
            data-testid="result-download-pdf"
            onClick={() => generateCredentialsPDF(exportData)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-red-600 hover:bg-red-700"
          >
            <Download className="w-4 h-4" /> Descargar PDF
          </button>
          <button
            data-testid="result-download-xlsx"
            onClick={() => generateCredentialsExcel(exportData)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-teal-600 hover:bg-teal-700"
          >
            <Download className="w-4 h-4" /> Descargar Excel
          </button>
          <button onClick={onClose} className="ml-auto px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-100">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
